
import React from 'react';
import { useEffect, useRef } from 'react';

interface VisualizerProps {
  analyser: AnalyserNode | null;
  isActive: boolean;
}

const Visualizer: React.FC<VisualizerProps> = ({ analyser, isActive }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!isActive) return;

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 1.5;

        // Semi-transparent Neon Gradient
        const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
        gradient.addColorStop(0, 'rgba(0, 210, 255, 0.9)'); // Bright Cyan
        gradient.addColorStop(1, 'rgba(58, 123, 213, 0.1)'); // Fade to transparent blue

        ctx.fillStyle = gradient;
        
        ctx.beginPath();
        ctx.roundRect(x, canvas.height - barHeight, barWidth, barHeight, [6, 6, 0, 0]);
        ctx.fill();

        x += barWidth + 1;
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyser, isActive]);

  return (
    <div className="w-full h-full relative group">
      {!isActive && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-groove-accent/70 font-black text-xs uppercase tracking-[0.5em] animate-pulse border-2 border-groove-accent/30 px-6 py-3 rounded-xl bg-black/60 backdrop-blur-md shadow-2xl">
            Signal Link Lost
          </div>
        </div>
      )}
      <canvas 
        ref={canvasRef} 
        width={600} 
        height={200} 
        className="w-full h-full object-cover"
      />
    </div>
  );
};

export default Visualizer;
